import { useAuditLogsQuery } from '../lib/Queries';
import Loading from './Loading';
import { formatDateTime } from '../lib/DateTime.js';
import { BoltIcon } from '@heroicons/react/24/outline/index.js';
import PencilSquareIcon from './icons/PencilSquareIcon';
import { isFuture } from 'date-fns';
import { truncate } from '../lib/Utils.js';
import { useState } from 'react';
import useBusinessPostApi from '~/lib/useBusinessPostApi.js';
import * as Toast from '~/lib/Toast.js';

function canResend(log) {
    return log.type === 'flow' && !isFuture(new Date(log.time));
}

const AuditLogEntry = ({ log, className, doResend, isResending }) => {
    const [isHidden, setIsHidden] = useState(log.isSkipped);

    return (
        <div className={`flex ${isHidden ? 'text-gray-500' : ''}`}>
            <div className="w-6 h-6 mr-2 mt-4">
                {log.type === 'flow' && <BoltIcon />}
                {log.type === 'audit' && <PencilSquareIcon />}
            </div>

            <div className={`flex-1 py-4 pr-4 ${className}`}>
                <div className={`flex items-center justify-between`}>
                    <div className="flex text-lg items-center capitalize">
                        {log.action}
                    </div>

                    {!isHidden && (
                        <div>
                            {isFuture(new Date(log.time)) && 'Upcoming at '}
                            {formatDateTime(log.time)}
                        </div>
                    )}
                    {isHidden && (
                        <div>
                            Skipped
                            <button
                                type="button"
                                className="btn-light-pill-sm uppercase ml-2"
                                onClick={() => setIsHidden(!isHidden)}
                            >
                                Show
                            </button>
                        </div>
                    )}
                </div>

                {!isHidden && (
                    <div className="text-gray-500 mt-2">
                        {!!log.subtext && (
                            <div className="text-gray-500 font-normal">
                                {truncate(log.subtext, 40)}
                            </div>
                        )}
                        {log.details?.length > 0 && (
                            <div>
                                {log.details.map((d, idx) => (
                                    <div
                                        key={idx}
                                        className={`leading-tight ${idx > 0 ? 'mt-1' : ''} ${d.startsWith('Failed') ? 'text-red-600' : ''}`}
                                    >
                                        {d}
                                    </div>
                                ))}
                            </div>
                        )}

                        {log.who ? `by ${log.who}` : ''}

                        {canResend(log) && (
                            <button
                                type="button"
                                className="btn-light-pill-sm uppercase mt-2"
                                onClick={() => doResend(log)}
                            >
                                {isResending ? 'Resending...' : 'Resend'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const AuditLogs = ({ type, id, className }) => {
    const { data, isLoading, refetch } = useAuditLogsQuery(type, id);
    const [resendingId, setResendingId] = useState();

    const { save: saveResend, isSaving: isResending } = useBusinessPostApi(
        `/flows/resend/${id}`,
    );

    function doResend(log) {
        if (!confirm('Are you sure you want to resend this?')) {
            return;
        }

        setResendingId(log.id);
        saveResend({
            id: log.id,
        }).then(result => {
            Toast.success('Successfully resent');
            setResendingId(null);
            refetch();
        });
    }

    if (!id) {
        return null;
    }

    return (
        <div className={`${className} panel pl-4`}>
            {isLoading && <Loading className="py-3" />}

            {!!data &&
                data.map((log, idx) => (
                    <AuditLogEntry
                        log={log}
                        key={idx}
                        className={idx > 0 ? 'border-t' : ''}
                        doResend={doResend}
                        isResending={log.id === resendingId && isResending}
                    />
                ))}
            {!isLoading && !data?.length && (
                <div className="py-3">There are no logs for this</div>
            )}
        </div>
    );
};

export default AuditLogs;
